import React from "react";
import DraggableStation from "../DraggableStation";
import DraggableSymbol from "../DraggableSymbol";
import CanvasRail from "../../../LiveDashboardModal/components/CanvasRail";
import LayerControls from "../../../LiveDashboardModal/components/LayerControls";
import DraggableTunnelLink from "../DraggableTunnelLink";
import { STATION_HALF_SIZE } from "../../utils/constants";
import { SYMBOL_HALF_SIZE, getSymbolById } from "../../utils/symbolTypes";
import { canvasMode, editorCursor } from "../../utils/editorCursor";

const TunnelCanvas = ({
  dimensions,
  canvasSize,
  viewport,
  placedStations,
  stationsVisible = true,
  selectedStationId,
  setSelectedStationId,
  handleTunnelDragStop,
  activeLayoutType,
  placedSymbols,
  symbolsVisible,
  handleSymbolDragStop,
  canvasRef,
  onCanvasClick,
  onDeleteSymbol,
  tripwireLayer = null,
  tripwiresVisible = true,
  // 圖層's 顯示 rows (editorLayerToggles) for this layout.
  layerToggles,
}) => {
  const upperLaneY = viewport.laneYPx("upper");
  const lowerLaneY = viewport.laneYPx("lower");
  const floorLeft = viewport.floorRect.left;
  const floorRight = floorLeft + viewport.floorRect.width;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div
        ref={canvasRef}
        style={{
          position: "absolute",
          left: 30,
          top: 60,
          width: `${canvasSize.width}px`,
          height: `${canvasSize.height}px`,
          // No 瀏覽 here: always editable, a tool crosshairs.
          cursor: editorCursor(canvasMode(true, !!onCanvasClick), "canvas"),
        }}
        onClick={onCanvasClick}
      >
        {/* Upper Lane */}
        <div
          style={{
            position: "absolute",
            top: upperLaneY,
            left: 0,
            width: "100%",
            height: 2,
            background: "linear-gradient(to right, #595959, #8c8c8c)",
            borderRadius: 2,
            boxShadow: "0 0 6px rgba(89, 89, 89, 0.35)",
          }}
        />
        {/* Lower Lane */}
        <div
          style={{
            position: "absolute",
            top: lowerLaneY,
            left: 0,
            width: "100%",
            height: 2,
            background: "linear-gradient(to right, #595959, #8c8c8c)",
            borderRadius: 2,
            boxShadow: "0 0 6px rgba(89, 89, 89, 0.35)",
          }}
        />

        {/* Tick marks on upper lane */}
        <div
          style={{
            position: "absolute",
            top: upperLaneY - 6,
            left: 0,
            width: 1,
            height: 12,
            backgroundColor: "#595959",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: upperLaneY - 6,
            left: canvasSize.width / 2,
            width: 1,
            height: 12,
            backgroundColor: "#595959",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: upperLaneY - 6,
            left: canvasSize.width - 1,
            width: 1,
            height: 12,
            backgroundColor: "#595959",
          }}
        />

        {/* Tick marks on lower lane */}
        <div
          style={{
            position: "absolute",
            top: lowerLaneY - 6,
            left: 0,
            width: 1,
            height: 12,
            backgroundColor: "#595959",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: lowerLaneY - 6,
            left: canvasSize.width / 2,
            width: 1,
            height: 12,
            backgroundColor: "#595959",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: lowerLaneY - 6,
            left: canvasSize.width - 1,
            width: 1,
            height: 12,
            backgroundColor: "#595959",
          }}
        />

        {/* Top tick text labels (width) */}
        <div
          style={{
            position: "absolute",
            top: upperLaneY - 24,
            left: 0,
            width: `${canvasSize.width}px`,
            fontSize: "12px",
            color: "#666",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 0,
              transform: "translateX(-50%)",
            }}
          >
            0m
          </span>
          <span
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            {(dimensions.width / 2).toFixed(1)}m
          </span>
          <span
            style={{
              position: "absolute",
              right: 0,
              transform: "translateX(50%)",
            }}
          >
            {dimensions.width}m
          </span>
        </div>

        {/* Bottom tick text labels */}
        <div
          style={{
            position: "absolute",
            top: lowerLaneY + 8,
            left: 0,
            width: `${canvasSize.width}px`,
            fontSize: "12px",
            color: "#666",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 0,
              transform: "translateX(-50%)",
            }}
          >
            0m
          </span>
          <span
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            {(dimensions.width / 2).toFixed(1)}m
          </span>
          <span
            style={{
              position: "absolute",
              right: 0,
              transform: "translateX(50%)",
            }}
          >
            {dimensions.width}m
          </span>
        </div>

        {/* Height indicator */}
        <div
          style={{
            position: "absolute",
            left: -25,
            top: upperLaneY,
            height: lowerLaneY - upperLaneY,
            display: "flex",
            alignItems: "center",
            fontSize: "11px",
            color: "#999",
          }}
        >
          <div
            style={{
              writingMode: "vertical-rl",
              textOrientation: "mixed",
            }}
          >
            {dimensions.height.toFixed(1)}m
          </div>
        </div>

        {/* Lane labels */}
        <div
          style={{
            position: "absolute",
            left: -25,
            top: upperLaneY - 6,
            fontSize: "10px",
            color: "#595959",
            fontWeight: "500",
          }}
        >
          上
        </div>
        <div
          style={{
            position: "absolute",
            left: -25,
            top: lowerLaneY - 6,
            fontSize: "10px",
            color: "#595959",
            fontWeight: "500",
          }}
        >
          下
        </div>

        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {symbolsVisible &&
            placedSymbols &&
            placedSymbols
              .filter((symbol) => {
                const symbolType = getSymbolById(symbol.symbol_type);
                return symbolType?.compatibleLayouts?.includes("tunnel");
              })
              .map((symbol) => {
                const symbolType = getSymbolById(symbol.symbol_type);

                if (symbolType?.renderType === "vertical_line") {
                  const xPixels =
                    viewport.place({
                      x: symbol.position_meters?.x,
                      y: dimensions.height / 2,
                    }).x - 2;

                  return (
                    <DraggableTunnelLink
                      key={symbol.id || `tunnel-link-${symbol.symbol_type}`}
                      symbol={symbol}
                      position={{
                        x: xPixels,
                        y: upperLaneY,
                      }}
                      bounds={{
                        left: floorLeft - 2,
                        right: floorRight - 2,
                        top: upperLaneY,
                        bottom: upperLaneY,
                      }}
                      upperLaneY={upperLaneY}
                      lowerLaneY={lowerLaneY}
                      isActive={activeLayoutType === "tunnel"}
                      onDragStop={handleSymbolDragStop}
                      onDelete={onDeleteSymbol}
                    />
                  );
                }

                const placed = viewport.place(symbol.position_meters);
                const xPixels = placed.x - SYMBOL_HALF_SIZE;
                const yPixels = placed.y - SYMBOL_HALF_SIZE;

                return (
                  <DraggableSymbol
                    key={symbol.id || `tunnel-symbol-${symbol.symbol_type}`}
                    symbol={symbol}
                    layoutType="tunnel"
                    activeLayoutType={activeLayoutType}
                    position={{
                      x: xPixels,
                      y: yPixels,
                    }}
                    bounds={{
                      left: floorLeft - SYMBOL_HALF_SIZE,
                      right: floorRight - SYMBOL_HALF_SIZE,
                      top: upperLaneY - SYMBOL_HALF_SIZE,
                      bottom: lowerLaneY - SYMBOL_HALF_SIZE,
                    }}
                    onDragStop={handleSymbolDragStop}
                    onDelete={onDeleteSymbol}
                  />
                );
              })}
          {stationsVisible &&
            placedStations.map((station) => {
              const placed = viewport.place(station.position_meters);
              const xPixels = placed.x - STATION_HALF_SIZE;
              const yPixels = placed.y - STATION_HALF_SIZE;

              return (
                <DraggableStation
                  key={
                    station.id ||
                    `tunnel-station-${station.device_id || station.name}`
                  }
                  station={station}
                  layoutType="tunnel"
                  activeLayoutType={activeLayoutType}
                  position={{
                    x: xPixels,
                    y: yPixels,
                  }}
                  bounds={{
                    left: floorLeft - STATION_HALF_SIZE,
                    right: floorRight - STATION_HALF_SIZE,
                    top: upperLaneY - STATION_HALF_SIZE,
                    bottom: lowerLaneY - STATION_HALF_SIZE,
                  }}
                  selectedStationId={selectedStationId}
                  setSelectedStationId={setSelectedStationId}
                  onDragStop={handleTunnelDragStop}
                  pxPerMeter={viewport.pxPerMeter}
                />
              );
            })}
        </div>
        {tripwiresVisible && tripwireLayer}
      </div>
      {layerToggles && (
        <CanvasRail>
          <LayerControls toggles={layerToggles} />
        </CanvasRail>
      )}
    </div>
  );
};

export default TunnelCanvas;
